import React, { useState, useEffect } from 'react';
import { useTheme } from '../src/hooks/useTheme';

interface ThemeSelectorProps {
  onClose?: () => void;
  onThemeChange?: (themeId: string) => void;
}

const ThemeSelector: React.FC<ThemeSelectorProps> = ({ onClose, onThemeChange }) => {
  const { theme: currentTheme, setTheme } = useTheme();
  const [selectedTheme, setSelectedTheme] = useState<string>(currentTheme);
  
  // テーマの色情報（プレビュー用）
  const themeColors: Record<string, { primary: string; secondary: string; bg: string }> = {
    modern: { primary: '#2563EB', secondary: '#3B82F6', bg: '#F8FAFC' },
    casual: { primary: '#eea730', secondary: '#f4c430', bg: '#ffffff' },
    natural: { primary: '#7aa652', secondary: '#8bb85a', bg: '#faf7f0' },
    gorgeous: { primary: '#d4af37', secondary: '#e6c547', bg: '#0f0f10' },
    minimal: { primary: '#4A5568', secondary: '#2D3748', bg: '#FFFFFF' },
    resort: { primary: '#38B2AC', secondary: '#319795', bg: '#E6FFFA' },
    vintage: { primary: '#D2691E', secondary: '#CD853F', bg: '#F7F3E9' },
    feminine: { primary: '#EC4899', secondary: '#F472B6', bg: '#FDF2F8' },
    industrial: { primary: '#64748B', secondary: '#475569', bg: '#F1F5F9' },
    nightLuxury: { primary: '#8B5CF6', secondary: '#A78BFA', bg: '#0F172A' }
  };
  
  const themes = [
    { id: 'modern', name: 'モダン', description: 'シンプルで洗練されたデザイン' },
    { id: 'casual', name: 'カジュアル', description: '親しみやすいカジュアルなデザイン' },
    { id: 'natural', name: 'ナチュラル', description: '自然で温かみのあるデザイン' },
    { id: 'gorgeous', name: 'ゴージャス', description: '高級感のある豪華なデザイン' },
    { id: 'minimal', name: 'ミニマル', description: '最小限の要素で構成されたデザイン' },
    { id: 'resort', name: 'リゾート', description: 'リラックスしたリゾート風デザイン' },
    { id: 'vintage', name: 'ヴィンテージ', description: 'レトロで温かみのあるデザイン' },
    { id: 'feminine', name: 'フェミニン', description: '優雅で女性らしいデザイン' },
    { id: 'industrial', name: 'インダストリアル', description: 'クールでモダンなインダストリアルデザイン' },
    { id: 'nightLuxury', name: 'ナイトラグジュアリー', description: '夜の高級感を演出するデザイン' }
  ];

  const handleThemeSelect = (themeId: string) => {
    setSelectedTheme(themeId);
  };

  const handleApply = () => {
    // テーマを設定（BroadcastChannel、storage、CSS適用が自動実行）
    setTheme(selectedTheme);
    
    // コールバックを呼び出し
    if (onThemeChange) {
      onThemeChange(selectedTheme);
    }
    
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* ヘッダー */}
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">テーマ選択</h2>
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            )}
          </div>
          <p className="text-sm mt-2 text-gray-600">
            お店の雰囲気に合ったテーマを選択してください
          </p>
        </div>

        {/* テーマ一覧 */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {themes.map((theme) => (
              <div
                key={theme.id}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                  selectedTheme === theme.id
                    ? 'border-blue-500 shadow-lg scale-105'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                }`}
                onClick={() => handleThemeSelect(theme.id)}
              >
                <div className="text-center">
                  <div 
                    className="w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center text-white font-bold text-lg"
                    style={{ 
                      background: `linear-gradient(135deg, ${themeColors[theme.id]?.primary || '#2563EB'}, ${themeColors[theme.id]?.secondary || '#3B82F6'})`
                    }}
                  >
                    {theme.name.charAt(0)}
                  </div>
                  <h3 className="font-semibold text-lg mb-1">{theme.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{theme.description}</p>
                  <div 
                    className="w-full h-4 rounded"
                    style={{ 
                      background: `linear-gradient(90deg, ${themeColors[theme.id]?.primary || '#2563EB'}, ${themeColors[theme.id]?.secondary || '#3B82F6'})`
                    }}
                  />
                  {/* 背景色プレビュー */}
                  <div 
                    className="w-full h-2 rounded mt-2"
                    style={{ 
                      backgroundColor: themeColors[theme.id]?.bg || '#F8FAFC'
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* フッター */}
        <div className="p-6 border-t flex justify-end space-x-3">
          {onClose && (
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              キャンセル
            </button>
          )}
          <button
            onClick={handleApply}
            className="px-6 py-2 rounded-md bg-red-800 text-white hover:bg-red-900 transition-colors"
          >
            適用
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThemeSelector;